import { useMutation, useQuery, useQueryClient } from 'react-query'
import { request } from 'Features/auth/axios-client'
import { UpdateUserTemplate } from './OperationalPlanner.types'

const fetchUserSettings = async (email: string) => {
  // fetch user settings from the server
  const { data } = await request({
    url: `/user-settings?email=${email}`,
    method: 'GET',
  })
  return data
}

export const useUserSettings = (email: string) => {
  return useQuery(['userSettings', email], () => fetchUserSettings(email), {
    enabled: !!email,
    select: (data) => {
      return data?.data?.userSettings
    },
    refetchOnWindowFocus: true,
  })
}

const updateUserSettings = async ({ email, settings }: UpdateUserTemplate) => {
  // update user settings on the server
  const { data } = await request({
    url: `/user-settings?email=${email}`,
    method: 'PUT',
    data: settings,
  })
  return data
}

export const useUpdateUserSettings = (onUpdateTemplateSuccess: () => void) => {
  const queryClient = useQueryClient()
  return useMutation(
    async (
      payload: UpdateUserTemplate & {
        variantName?: string
        isNewVariant?: boolean
        isUpdatingOwnVariant?: boolean
        targetUserEmail?: string
      }
    ) => {
      const {
        email,
        settings,
        variantName,
        isNewVariant,
        isUpdatingOwnVariant,
        targetUserEmail,
      } = payload

      if (isNewVariant && variantName) {
        // Create new variant for user
        return createNewVariant({
          email,
          variantName,
          variantData: settings,
        })
      } else if (isUpdatingOwnVariant && variantName) {
        // Update variant (either own or cross-user)
        return updateOwnVariant({
          email,
          variantName,
          variantData: settings,
          targetUserEmail,
        })
      } else {
        // Fallback: Update full settings object (legacy/other cases)
        return updateUserSettings({ email, settings })
      }
    },
    {
      onSuccess: (_, variables) => {
        // invalidate the user settings query to refetch the updated data
        queryClient.invalidateQueries(['userSettings', variables.email])
        onUpdateTemplateSuccess()
      },
    }
  )
}

export const useDeleteVariant = (
  onDeleteSuccess: () => void,
  onDeleteError?: () => void
) => {
  const queryClient = useQueryClient()
  return useMutation(
    async ({ email, variantName }: { email: string; variantName: string }) => {
      // Delete own variant using DELETE API
      return deleteOwnVariant({ email, variantName })
    },
    {
      onSuccess: (_, variables) => {
        // invalidate the user settings query to refetch the updated data
        queryClient.invalidateQueries(['userSettings', variables.email])
        onDeleteSuccess()
      },
      onError: () => {
        onDeleteError?.()
      },
    }
  )
}

const createNewVariant = async ({
  email,
  variantName,
  variantData,
}: {
  email: string
  variantName: string
  variantData: any
}) => {
  const { data } = await request({
    url: `/user-settings?email=${email}&variantName=${variantName}`,
    method: 'POST',
    data: variantData,
  })
  return data
}

const updateOwnVariant = async ({
  email,
  variantName,
  variantData,
  targetUserEmail,
}: {
  email: string
  variantName: string
  variantData: any
  targetUserEmail?: string
}) => {
  // Build URL with conditional targetUserEmail parameter
  let url = `/user-settings?email=${email}&variantName=${variantName}`
  if (targetUserEmail) {
    url += `&targetUserEmail=${targetUserEmail}`
  }

  const { data } = await request({
    url,
    method: 'PUT',
    data: variantData,
  })
  return data
}

const deleteOwnVariant = async ({
  email,
  variantName,
}: {
  email: string
  variantName: string
}) => {
  const { data } = await request({
    url: `/user-settings?email=${email}&variantName=${variantName}`,
    method: 'DELETE',
  })
  return data
}
